import { InputNumber, InputNumberProps } from 'antd';
import i18next from 'i18next';
import { ComponentProps, useMemo, useSyncExternalStore } from 'react';

export const decimalSeparatorFor = (language: string) => (language.toLowerCase().startsWith('el') ? ',' : '.');

// The locale's decimal separator is the decimal point; the other one is decimal only when it occurs
// once with 1-2 digits after it (en `12,5`), otherwise it groups thousands (en `1,500` = 1500).
// `grouping: false` is for values that never reach a thousand (percents): any lone separator is decimal.
export const parseMoneyInput = (
  raw: string | undefined,
  language: string,
  { grouping = true }: { grouping?: boolean } = {}
): string => {
  const cleaned = (raw ?? '').replace(/[^\d.,-]/g, '');
  const negative = cleaned.startsWith('-');
  const unsigned = cleaned.replace(/-/g, '');
  const localeDecimal = decimalSeparatorFor(language);
  const other = localeDecimal === ',' ? '.' : ',';
  const hasLocale = unsigned.includes(localeDecimal);
  const hasOther = unsigned.includes(other);

  let separator = -1;
  if (hasLocale && hasOther) {
    // Mixed (`1.234,56`): the later one is the decimal point.
    separator = Math.max(unsigned.lastIndexOf(','), unsigned.lastIndexOf('.'));
  } else if (hasLocale) {
    if (unsigned.split(localeDecimal).length === 2) separator = unsigned.indexOf(localeDecimal);
  } else if (hasOther) {
    const parts = unsigned.split(other);
    if (parts.length === 2 && (!grouping || /^\d{1,2}$/.test(parts[1]))) separator = unsigned.indexOf(other);
  }

  const sign = negative ? '-' : '';
  if (separator < 0) return unsigned ? sign + unsigned.replace(/[.,]/g, '') : '';
  const whole = unsigned.slice(0, separator).replace(/[.,]/g, '');
  const fraction = unsigned.slice(separator + 1).replace(/[.,]/g, '');
  return `${sign}${whole}.${fraction}`;
};

// The i18next instance directly, not `@app/i18n`: importing that initialises i18n, which breaks
// every test that mocks react-i18next.
const subscribe = (onChange: () => void) => {
  i18next.on('languageChanged', onChange);
  return () => i18next.off('languageChanged', onChange);
};
const getLanguage = () => i18next.language ?? '';
export const useInputLanguage = () => useSyncExternalStore(subscribe, getLanguage, getLanguage);

export const useMoneyInputProps = () => {
  const language = useInputLanguage();
  // rc-input-number parses the returned decimal string itself ('' clears the field); antd types it as T.
  const parser = useMemo(
    () =>
      ((raw: string | undefined) => parseMoneyInput(raw, language)) as unknown as NonNullable<
        InputNumberProps<number>['parser']
      >,
    [language]
  );
  return {
    precision: 2,
    decimalSeparator: decimalSeparatorFor(language),
    parser,
  } satisfies InputNumberProps<number>;
};

const MoneyInput = (props: ComponentProps<typeof InputNumber<number>>) => {
  const moneyProps = useMoneyInputProps();
  return <InputNumber<number> {...moneyProps} {...props} />;
};

export default MoneyInput;
